import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fmtFCFA } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { Bed, Bath, Maximize, MapPin, Heart } from "lucide-react";
import { toast } from "sonner";

const BADGE_STYLES = {
  "Nouveau": "bg-emerald-600 text-white",
  "Exclusivité": "bg-gold text-navy",
  "À la une": "bg-navy text-white",
  "Bon investissement": "bg-amber-500 text-white",
};

export const PropertyCard = ({ property, favored = false, onFavChange }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const [fav, setFav] = useState(favored);
  useEffect(() => setFav(favored), [favored]);

  const toggleFav = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Connectez-vous pour ajouter aux favoris."); return; }
    try {
      if (fav) { await api.delete(`/favorites/${property.id}`); setFav(false); }
      else { await api.post(`/favorites/${property.id}`); setFav(true); }
      onFavChange?.();
    } catch { toast.error("Action impossible."); }
  };

  const isNight = property.short_term && property.price_night;

  return (
    <Link
      to={`/bien/${property.id}`}
      data-testid={`property-card-${property.reference}`}
      className="group block overflow-hidden rounded-xl bg-white card-hover border border-black/5"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={property.images?.[0]} alt={property.title} className="h-full w-full object-cover img-zoom" loading="lazy" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="rounded-md bg-white/95 px-2.5 py-1 text-xs font-semibold text-navy">{t(`status.${property.status}`)}</span>
          {property.badge && (
            <span className={`rounded-md px-2.5 py-1 text-xs font-semibold ${BADGE_STYLES[property.badge] || "bg-navy text-white"}`}>
              {t(`badge.${property.badge}`)}
            </span>
          )}
        </div>
        <button onClick={toggleFav} data-testid={`fav-btn-${property.reference}`}
          className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-navy hover:scale-110 transition-transform">
          <Heart className={`h-4.5 w-4.5 ${fav ? "fill-red-500 text-red-500" : ""}`} />
        </button>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-wider text-gold">{property.reference}</span>
          <span className="text-xs text-slate-400">{property.type}</span>
        </div>
        <h3 className="mt-1.5 font-sans text-lg font-semibold text-navy line-clamp-1">{property.title}</h3>
        <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
          <MapPin className="h-3.5 w-3.5" /> {property.district ? `${property.district}, ` : ""}{property.city}, {property.country}
        </p>
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-600">
          {property.surface > 0 && <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4 text-gold" /> {property.surface} m²</span>}
          {property.bedrooms > 0 && <span className="flex items-center gap-1.5"><Bed className="h-4 w-4 text-gold" /> {property.bedrooms}</span>}
          {property.bathrooms > 0 && <span className="flex items-center gap-1.5"><Bath className="h-4 w-4 text-gold" /> {property.bathrooms}</span>}
        </div>
        <div className="mt-4 flex items-end justify-between border-t border-dashed pt-4">
          <div>
            <span className="font-mono text-xl font-bold text-navy">{fmtFCFA(isNight ? property.price_night : property.price)}</span>
            {isNight && <span className="text-xs text-slate-400"> {t("common.perNight")}</span>}
            {property.transaction === "location" && !isNight && <span className="text-xs text-slate-400"> {t("common.perMonth")}</span>}
          </div>
          <span className="text-sm font-medium text-gold group-hover:translate-x-1 transition-transform">{t("common.seeProperty")}</span>
        </div>
      </div>
    </Link>
  );
};
